/**
 * Shared search/filter/sort semantics for unbilled WIP.
 *
 * Live mode sends this state to `GET /api/billing/{projectId}/wip`, which applies
 * it across the whole project before paging. Demo mode has no API, so the same
 * rules are applied here over the mock rows — keeping the two surfaces (the
 * Unbilled Work table and the Edit-Draft "Add Lines" picker) consistent.
 */

export type WipSortDirection = 'asc' | 'desc';

export interface WipColumnSort {
  columnId: string;
  direction: WipSortDirection;
}

/** Optional server-side query state accepted by the paged WIP endpoint. */
export interface WipQueryOptions {
  search?: string;
  sortBy?: string;
  sortDirection?: WipSortDirection;
  sourceTypes?: number[];
  resources?: string[];
  /** Inclusive `yyyy-MM-dd` lower bound. */
  dateFrom?: string;
  /** Inclusive `yyyy-MM-dd` upper bound. */
  dateTo?: string;
}

/** The filter/search/sort state held by the Unbilled Work table. */
export interface WipFilterState {
  search: string;
  /** Filter-sheet entry type: `all-types`, `time`, `expense` or `invoice`. */
  type: string;
  resources: string[];
  dateFrom?: Date;
  dateTo?: Date;
  sorts: WipColumnSort[];
}

/** Row fields the filters and sorts read. */
export interface WipFilterableRow {
  date: string;
  resource: string;
  task?: string;
  description: string;
  type: string;
  quantity: number;
  amount: number;
  costCentre?: string;
  reference?: string;
}

export const ALL_WIP_TYPES = 'all-types';
export const ALL_WIP_RESOURCES = 'all-resources';

/** Entry-type filter → WIP ledger SourceType. "Expense" is SourceType 2 only. */
export const WIP_TYPE_FILTER_SOURCE_TYPES: Record<string, number[]> = {
  time: [1],
  expense: [2],
  invoice: [4],
};

/** Columns the stored procedure can sort by (mirrors `WipLedgerQuery.SortableColumns`). */
export const SERVER_SORTABLE_WIP_COLUMNS: readonly string[] = [
  'date',
  'resource',
  'task',
  'description',
  'type',
  'quantity',
  'rate',
  'amount',
  'costCentre',
];

export const DEFAULT_WIP_FILTER_STATE: WipFilterState = {
  search: '',
  type: ALL_WIP_TYPES,
  resources: [],
  dateFrom: undefined,
  dateTo: undefined,
  sorts: [{ columnId: 'date', direction: 'desc' }],
};

/** Local-time `yyyy-MM-dd`, so a date picked in the UI is not shifted by the UTC offset. */
export function formatWipDateOnly(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function hasActiveWipFilters(state: WipFilterState): boolean {
  return (
    state.search.trim() !== '' ||
    state.type !== ALL_WIP_TYPES ||
    state.resources.length > 0 ||
    state.dateFrom !== undefined ||
    state.dateTo !== undefined
  );
}

/**
 * Maps the table's filter state onto the endpoint's query options. Only the
 * primary sort is sent: the stored procedure sorts on one column plus stable
 * tie-breakers.
 */
export function toWipQueryOptions(state: WipFilterState): WipQueryOptions {
  const options: WipQueryOptions = {};

  const search = state.search.trim();
  if (search !== '') options.search = search;

  const sourceTypes = WIP_TYPE_FILTER_SOURCE_TYPES[state.type.toLowerCase()];
  if (sourceTypes) options.sourceTypes = [...sourceTypes];

  const resources = state.resources.filter((r) => r.trim() !== '' && r !== ALL_WIP_RESOURCES);
  if (resources.length > 0) options.resources = resources;

  if (state.dateFrom) options.dateFrom = formatWipDateOnly(state.dateFrom);
  if (state.dateTo) options.dateTo = formatWipDateOnly(state.dateTo);

  const primarySort = state.sorts[0];
  if (primarySort && SERVER_SORTABLE_WIP_COLUMNS.includes(primarySort.columnId)) {
    options.sortBy = primarySort.columnId;
    options.sortDirection = primarySort.direction;
  }

  return options;
}

/**
 * Splits a search term the way the stored procedure does: on any whitespace,
 * including the line breaks and non-breaking spaces that Financials narratives
 * carry into descriptions but that the grid collapses when it renders them.
 */
export function splitWipSearchTokens(search: string): string[] {
  return search
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token !== '');
}

/** Every token must match at least one field, though different tokens may match different fields. */
export function matchesWipSearchTokens(fields: (string | undefined)[], tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const haystacks = fields.map((field) => (field ?? '').toLowerCase());
  return tokens.every((token) => haystacks.some((haystack) => haystack.includes(token)));
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Demo-mode equivalent of the stored procedure's WHERE clause. */
export function filterWipRows<T extends WipFilterableRow>(rows: T[], state: WipFilterState): T[] {
  const tokens = splitWipSearchTokens(state.search);
  const resources = state.resources.filter((r) => r !== '' && r !== ALL_WIP_RESOURCES).map((r) => r.toLowerCase());

  return rows.filter((row) => {
    const matchesSearch = matchesWipSearchTokens(
      [row.description, row.resource, row.task, row.costCentre, row.reference],
      tokens
    );

    const matchesType = state.type === ALL_WIP_TYPES || row.type.toLowerCase() === state.type.toLowerCase();

    const matchesResource = resources.length === 0 || resources.includes(row.resource.toLowerCase());

    const rowDate = startOfDay(new Date(row.date));
    const matchesDateFrom = !state.dateFrom || rowDate >= startOfDay(state.dateFrom);
    const matchesDateTo = !state.dateTo || rowDate <= startOfDay(state.dateTo);

    return matchesSearch && matchesType && matchesResource && matchesDateFrom && matchesDateTo;
  });
}

/** Demo-mode equivalent of the stored procedure's ORDER BY (multi-column locally). */
export function sortWipRows<T extends WipFilterableRow>(rows: T[], sorts: WipColumnSort[]): T[] {
  return [...rows].sort((a, b) => {
    for (const sort of sorts) {
      const key = sort.columnId as keyof T;
      let aValue: unknown = a[key];
      let bValue: unknown = b[key];

      if (sort.columnId === 'date') {
        aValue = new Date(String(aValue)).getTime();
        bValue = new Date(String(bValue)).getTime();
      }

      let cmp = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        cmp = aValue - bValue;
      } else {
        const aStr = String(aValue ?? '').toLowerCase();
        const bStr = String(bValue ?? '').toLowerCase();
        cmp = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      }
      if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
}

export function filterAndSortWipRows<T extends WipFilterableRow>(rows: T[], state: WipFilterState): T[] {
  return sortWipRows(filterWipRows(rows, state), state.sorts);
}

export interface WipRowsSummary {
  totalCount: number;
  totalNetAmount: number;
  timeNetAmount: number;
  expenseNetAmount: number;
  timeHours: number;
}

/**
 * Qty column total for the Unbilled Work grid: sums the displayed quantity of every row,
 * across all transaction types, so it describes the same rows as the Amount total.
 */
export function sumWipQuantity(rows: Pick<WipFilterableRow, 'quantity'>[]): number {
  return rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
}

/** Demo-mode equivalent of the stored procedure's filtered aggregates. */
export function summariseWipRows(rows: WipFilterableRow[]): WipRowsSummary {
  return rows.reduce<WipRowsSummary>(
    (summary, row) => ({
      totalCount: summary.totalCount + 1,
      totalNetAmount: summary.totalNetAmount + row.amount,
      timeNetAmount: summary.timeNetAmount + (row.type === 'Time' ? row.amount : 0),
      expenseNetAmount: summary.expenseNetAmount + (row.type === 'Expense' ? row.amount : 0),
      timeHours: summary.timeHours + (row.type === 'Time' ? row.quantity : 0),
    }),
    { totalCount: 0, totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 }
  );
}
